import React from 'react';
import ServiceLayout from '../../../components/layout/ServiceLayout';
import Hero from '../../../components/sections/Hero';
import Card from '../../../components/ui/Card';
import FAQ from '../../../components/ui/FAQ';
import CallToAction from '../../../components/sections/CallToAction';
import { faqs, targetAudiences, services } from '../../../data/mockData';
import ServiceCard from '../../../components/ui/ServiceCard';
import FeatureCard from '../../../components/ui/FeatureCard';

const SmallBusinessPage: React.FC = () => {
  // Get small business audience data
  const smallBusinessData = targetAudiences.find(audience => audience.id === 'small-business');
  
  // Filter relevant services for small businesses
  const relevantServices = services.filter(service => 
    ['ai-onboarding', 'workflow-automation', 'ai-training'].includes(service.id)
  );

  // Filter relevant FAQs
  const relevantFaqs = faqs.filter((_, index) => [0, 1, 2].includes(index));
  
  return (
    <ServiceLayout>
      <Hero
        title="AI Solutions for Small Businesses"
        subtitle="Level the playing field with enterprise-grade AI solutions scaled to fit your small business needs and budget."
        ctaText="Explore Small Business Solutions"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Value Proposition Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Unlock AI's Power Without Enterprise Budgets</h2>
            <p className="text-lg text-gray-700">
              Small businesses face unique challenges: limited resources, small teams, and tough competition from larger players. 
              Our affordable, right-sized AI solutions help you compete effectively by automating routine tasks, uncovering valuable 
              customer insights, and making your team more productive and efficient.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Compete with Larger Businesses</h3>
              <p>
                Access the same advanced AI capabilities used by enterprise companies, but with solutions
                tailored to your size, budget, and specific business needs.
              </p>
            </Card>
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Do More with Your Small Team</h3>
              <p>
                Multiply your team's productivity by automating repetitive tasks and streamlining operations,
                so your limited staff can focus on high-value activities.
              </p>
            </Card>
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Affordable, Transparent Pricing</h3>
              <p>
                Our small business packages are designed to deliver maximum impact without breaking the bank,
                with clear pricing and predictable monthly costs.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Small Business Solutions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Tailored Solutions for Small Business Success</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {smallBusinessData && (
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-2xl font-semibold mb-4">{smallBusinessData.title}</h3>
                <p className="mb-6 text-gray-700">{smallBusinessData.description}</p>
                <ul className="space-y-3 mb-8">
                  {smallBusinessData.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-primary font-bold mr-2">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="bg-primary bg-opacity-5 p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4">Common Small Business Challenges We Solve</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-xl font-medium mb-2">Limited Resources</h4>
                  <p className="text-gray-700">
                    Stretch your budget with AI tools that deliver enterprise-level capabilities at a fraction of the cost of hiring additional staff.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">Administrative Overhead</h4>
                  <p className="text-gray-700">
                    Reduce time spent on bookkeeping, scheduling, customer support, and other administrative tasks through smart automation.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">Customer Acquisition</h4>
                  <p className="text-gray-700">
                    Leverage AI for targeted marketing, personalized customer experiences, and higher conversion rates without a large marketing team.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">Fierce Competition</h4>
                  <p className="text-gray-700">
                    Stand out from competitors with enhanced products, services, and customer experiences powered by AI innovation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Packages for Small Business */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Small Business AI Packages</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              title="Small Business Automation Essentials"
              description="A starter package of essential automation tools to streamline your operations and free up valuable time and resources."
              icon="settings-automation"
              details={[
                "Email and communication automation",
                "Document processing automation",
                "Basic customer service automation",
                "Simple workflow automation",
                "Initial team training"
              ]}
            />
            <FeatureCard
              title="Customer Experience AI Package"
              description="Enhance your customer interactions with AI-powered tools that create personalized, responsive experiences with minimal staff."
              icon="users"
              details={[
                "AI-powered customer chat system",
                "Personalized marketing automation",
                "Customer feedback analysis",
                "Behavioral insights dashboard",
                "Customer journey optimization"
              ]}
            />
            <FeatureCard
              title="Small Business Growth Accelerator"
              description="A comprehensive AI package designed to help small businesses identify and capitalize on growth opportunities efficiently."
              icon="trending-up"
              details={[
                "Market opportunity analysis",
                "Competitor intelligence automation",
                "Lead generation and qualification",
                "Dynamic pricing optimization",
                "Business performance dashboard"
              ]}
            />
          </div>
        </div>
      </section>

      {/* Relevant Services Section */}
      {relevantServices.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Our Services for Small Businesses</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {relevantServices.map(service => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Success Metrics */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Real Results for Small Businesses</h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">20+</div>
              <p className="text-xl">Hours saved weekly through automation</p>
            </div>
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">43%</div>
              <p className="text-xl">Average increase in customer response speed</p>
            </div>
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">3.2x</div>
              <p className="text-xl">Return on investment within the first year</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto">
            <FAQ items={relevantFaqs} />
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <CallToAction
        title="Ready to give your small business an AI advantage?"
        description="Contact us today to discuss your business challenges and how our small business AI solutions can help you compete, grow, and thrive."
        ctaText="Get Started"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default SmallBusinessPage;